// @flow
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Typography } from 'src/latest/src/components';
import Button from '../../components/base/Button/Button';
import laptop from '../../assets/icons/icon-laptop-phone.svg';
import './Intakegreetings.scss';
import { useDispatch, useSelector } from 'react-redux';
import { saveResponses } from '../../redux/actions/questionnaireActions';
import { prepareResponseData } from '../../legacy/utils/helpers';
import { setUserToBeOnboarded } from 'src/redux/actions/onboardingActions';
import { PageUniqueKeys } from 'src/containers/QuestionnaireParent/QuestionnaireEnum';

const THERAPIST_LAYOUT_PAGES = [
  PageUniqueKeys.therapistOnboardingBySchedulerCompletionWelcomeScreenV1,
  PageUniqueKeys.clientOnboardingBySchedulerWelcomePage,
  PageUniqueKeys.clientOnboardingWelcomePage,
  PageUniqueKeys.intakeCompletionWelcomeScreen,
  PageUniqueKeys.intakeCompletionWelcomeScreenDirectoryV1,
  PageUniqueKeys.intakeCompletionWelcomeScreenVidaV1,
];

const THERAPIST_COMPLETION_PAGES = [
  PageUniqueKeys.intakeCompletionWelcomeScreen,
  PageUniqueKeys.intakeCompletionWelcomeScreenDirectoryV1,
];

const ADMIN_COMPLETION_PAGES = [
  PageUniqueKeys.PracticeAdminIntakeCompletionWelcomeScreenV1,
  PageUniqueKeys.schedulerIntakeCompletionWelcomeScreenV1,
];

export default function IntakeWelcomeScreen(): React$Element<any> {
  const { t } = useTranslation('intakeWelcomeScreen');
  const userRole = useSelector(
    state => state.authentication.loggedInUser.userRole,
  );
  const clientType = useSelector(
    state => state.authentication.loggedInUser.metadata?.['client-type'],
  );
  const dispatch = useDispatch();
  const currentQuestions = useSelector(
    state => state?.questionnaireData?.currentConfig?.currentQuestions,
  );
  const sessionId = useSelector(
    state => state?.questionnaireData?.currentConfig?.questionnaireSessionId,
  );
  const categoryId = useSelector(
    state => state?.questionnaireData?.currentConfig?.currentCategory?.id,
  );
  const currentPage = useSelector(
    state => state?.questionnaireData?.currentConfig?.currentPage,
  );
  const [clickedOnce, setClickedOnce] = useState(false);
  const tenantLogo = useSelector(state => state?.configuration?.tenantLogo);
  const fetchingAppConfig = useSelector(
    state => state?.configuration?.fetchingAppConfig,
  );
  const tenantContact = useSelector(
    state => state?.configuration?.tenantContact,
  );

  const tenantName = useSelector(state => state?.configuration?.tenantName);
  const isDirectory = useSelector(
    (state: any) => state.configuration.isDirectoryPlatform,
  );

  const isDirectoryPlatform = isDirectory === 'true';

  const isClient = userRole === 'client';
  const isNewClient = isClient && clientType === 'NEW_CLIENT_INTAKE';
  const isExistingClient = isClient && clientType === 'EXISTING_CLIENT_INTAKE';

  const currentPageUniqueKey = currentPage?.unique_key;
  const isTherapistBySchedulerPage =
    currentPageUniqueKey ===
    PageUniqueKeys.therapistOnboardingBySchedulerCompletionWelcomeScreenV1;
  const isClientBySchedulerPage =
    currentPageUniqueKey ===
    PageUniqueKeys.clientOnboardingBySchedulerWelcomePage;
  const isBySchedulerPage =
    isTherapistBySchedulerPage || isClientBySchedulerPage;
  const isNewClientCompletionPage =
    currentPageUniqueKey ===
    PageUniqueKeys.intakeCompletionNewClientWelcomeScreenV1;
  const isSelfMatchingCompletionPage =
    currentPageUniqueKey ===
    PageUniqueKeys.intakeCompletionSelfMatchingClientWelcomeScreenV1;
  const isTherapistCompletionPage =
    THERAPIST_COMPLETION_PAGES.includes(currentPageUniqueKey);
  const isVidaCompletionPage =
    currentPageUniqueKey === PageUniqueKeys.intakeCompletionWelcomeScreenVidaV1;
  const isAdminCompletionPage =
    ADMIN_COMPLETION_PAGES.includes(currentPageUniqueKey);
  const usesTherapistLayout =
    THERAPIST_LAYOUT_PAGES.includes(currentPageUniqueKey);

  const showNewClientText = isNewClient && !isNewClientCompletionPage;
  const showMeetingInfoText =
    (isExistingClient && !isNewClientCompletionPage) ||
    (isClient && isSelfMatchingCompletionPage);
  const showNewClientCompletionText = isClient && isNewClientCompletionPage;

  const handleOnclick = async () => {
    setClickedOnce(true);
    const response = prepareResponseData(currentQuestions);
    const hasChangedResponses: boolean = true;

    dispatch(
      saveResponses({
        sessionId,
        categoryId,
        pageId: currentPage?.id,
        responses: response,
        hasChangedResponses,
      }),
    );

    dispatch(setUserToBeOnboarded(null));
  };

  return (
    <div className="welcome-greetings">
      <div className="welcome-greetings__logo">
        {!fetchingAppConfig && (
          <img
            style={{ width: 'auto', height: '100px' }}
            src={`${decodeURIComponent(tenantLogo)}`}
            alt={t('tenantLogoAlt')}
          />
        )}
      </div>
      <div
        className={`mb-5 ${
          usesTherapistLayout
            ? 'welcome-greetings__therapist'
            : 'welcome-greetings__contents'
        }`}
      >
        <div className="welcome-greetings__contents-wrapper">
          <Typography
            color="color-2"
            weight="light"
            variant="size-26"
            className="text-center "
          >
            {isExistingClient
              ? t('heading.profileUpdated')
              : isBySchedulerPage
              ? t('heading.almostDone')
              : isVidaCompletionPage
              ? t('heading.profileCompleted')
              : t('heading.profileCreated')}
          </Typography>
          {/* <Typography
                        color='color-2'
                        weight='regular' variant='size-18' className='greetings__contents-wrapper__text-first'>
                                We'll start by asking you a bunch of questions. This is important for creating your profile, so take your time and answer honestly. The more you help us get to know you, the better insights we can provide to assist in the matching process.

                    </Typography> */}
          {showNewClientText && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              {t('client.newClient', { tenantName, tenantContact })}
            </Typography>
          )}
          {showMeetingInfoText && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              <Trans
                t={t}
                i18nKey="client.meetingInfo"
                components={{ bold: <b />, italic: <i /> }}
              />
            </Typography>
          )}
          {showNewClientCompletionText && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              {t('client.newClientCompletion')}
            </Typography>
          )}
          {isTherapistBySchedulerPage && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              {t('therapist.onboardedByScheduler')}
            </Typography>
          )}
          {isTherapistCompletionPage && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              {isDirectoryPlatform
                ? t('therapist.completionDirectory')
                : t('therapist.completion')}
            </Typography>
          )}
          {isVidaCompletionPage && (
            <>
              <Typography
                color="color-2"
                weight="regular"
                variant="size-18"
                className="text-center welcome-greetings__contents-wrapper__text"
              >
                {t('therapist.vida.nextStep')}
              </Typography>
              <Typography
                color="color-2"
                weight="regular"
                variant="size-18"
                className="text-center welcome-greetings__contents-wrapper__text"
              >
                <Trans
                  t={t}
                  i18nKey="therapist.vida.whatHappensNext"
                  components={{ bold: <b /> }}
                />
              </Typography>
              <Typography
                color="color-2"
                weight="regular"
                variant="size-18"
                className="text-center welcome-greetings__contents-wrapper__text"
              >
                <Trans
                  t={t}
                  i18nKey="therapist.vida.profileEditsAndPrivacy"
                  components={{ bold: <b /> }}
                />
              </Typography>
            </>
          )}
          {isClientBySchedulerPage && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center client_OnboardingBySchedulerWelcomePage"
            >
              {t('scheduler.clientOnboarded')}
            </Typography>
          )}
          {isAdminCompletionPage && (
            <Typography
              color="color-2"
              weight="regular"
              variant="size-18"
              className="text-center welcome-greetings__contents-wrapper__text"
            >
              {t('admin.completion')}
            </Typography>
          )}
          {!isBySchedulerPage && (
            <>
              <span className="welcome-greetings__contents-wrapper__separator" />
              <img src={laptop} alt={t('device.imageAlt')} />
              <Typography
                color="color-2"
                weight="regular"
                variant="size-18"
                className="welcome-greetings__contents-wrapper__text text-center mt-4"
              >
                {t('device.recommendation')}
              </Typography>
            </>
          )}
        </div>
        <Button
          label={clickedOnce ? t('buttons.submitting') : t('buttons.submit')}
          isProcessing={clickedOnce}
          onClick={handleOnclick}
          disabled={clickedOnce}
        />
      </div>
      <div className="d-flex justify-content-center">
        {!isTherapistBySchedulerPage && (
          <div className="welcome-greetings__footer">
            <Typography
              color="color-28"
              weight="regular"
              variant="size-14"
              className="text-center welcome-greetings__footer-text"
            >
              {t('footer.platformDisclaimer')}
            </Typography>
            <Typography
              color="color-28"
              weight="regular"
              variant="size-14"
              className="text-center"
            >
              {t('footer.endorsementDisclaimer')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
